import React, { useState } from "react";
import {
    addMonths, subMonths, startOfMonth, endOfMonth,
    isWithinInterval, isSameDay, eachDayOfInterval, format, compareAsc, isToday
} from "date-fns";
import {et} from "date-fns/locale";
import CloseIcon from '@mui/icons-material/Close';

interface Availability {
    startDate: Date;
    endDate: Date;
}

interface CalendarProps {
    availability: Availability[];
    closeModal: () => void;
}

const AvailabilityCalendar: React.FC<CalendarProps> = ({ availability, closeModal }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    // Get first and last day of the month
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);

    // Generate days for the month (1-31)
    const days = eachDayOfInterval({ start, end });

    // Get availability for a selected date
    const getAvailabilityForDate = (date: Date) => {
        const intervals = availability
            .filter(interval => isWithinInterval(date, {start: interval.startDate, end: interval.endDate})
                || isSameDay(date, interval.startDate) || isSameDay(date, interval.endDate))
            .sort((a, b) => compareAsc(a.startDate, b.startDate)); // Sort by start time

        if (intervals.length === 0) {
            return "Pole saadaval";
        }

        // If fully available
        // const isFullyAvailable = intervals.some(interval => isWithinInterval(date,
        //     {start: interval.startDate, end: interval.endDate}));
        // if (isFullyAvailable) {
        //     return "Available entire day";
        // }

        // If partially available
        return intervals.map(interval => {
            if (isSameDay(date, interval.startDate) && isSameDay(date, interval.endDate)) {
                return `Alates ${format(interval.startDate, "HH:mm")} kuni ${format(interval.endDate, "HH:mm")}`;
            } else if (isSameDay(date, interval.startDate)) {
                return `Alates ${format(interval.startDate, "HH:mm")}`;
            } else if (isSameDay(date, interval.endDate)) {
                return `Kuni ${format(interval.endDate, "HH:mm")}`;
            } else if (isWithinInterval(date,
                {start: interval.startDate, end: interval.endDate})) {
                return "Saadaval terve päeva";
            }
            return "";
        }).join(", ");
    };

    // Determine tile color
    const getTileData = (date: Date) => {
        let fullyAvailable = false;
        let partlyAvailable = false;

        for (const interval of availability) {
            if (isSameDay(date, interval.startDate) || isSameDay(date, interval.endDate)) {
                partlyAvailable = true;
            } else if (isWithinInterval(date, {start: interval.startDate, end: interval.endDate})) {
                fullyAvailable = true;
            }
        }

        if (fullyAvailable) return "bg-green-400";
        if (partlyAvailable) return "bg-yellow-400";
        return "bg-red-400";
    };

    return (
        <div className="w-full max-w-md mx-auto p-4 bg-white shadow-lg rounded-lg relative">
            <div className="text-xl font-bold pb-2">Saadavus</div>
            <div className="absolute right-0 top-0 p-3 hover:text-primary cursor-pointer" onClick={() =>
                closeModal()}>
                <CloseIcon style={{width: "24px", height: "24px"}}/>
            </div>
            {/* Month Navigation */}
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 bg-gray-200 rounded">←</button>
                <h2 className="text-lg font-bold">{format(currentMonth, "MMMM yyy", {locale: et})}</h2>
                <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 bg-gray-200 rounded">→</button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                    const tileColor = getTileData(day);
                    return (
                        <div
                            key={day.toISOString()}
                            className={`p-4 text-center text-white cursor-pointer ${tileColor} ${selectedDate && isSameDay(day, selectedDate) ? "ring-2 ring-black" : ""}`}
                            onClick={() => setSelectedDate(day)}
                        >
                            <span className={`${isToday(day) ? "font-bold underline" : ""} block`}>{day.getDate()}</span>
                        </div>
                    );
                })}
            </div>

            {/* Selected Date Details */}
            {!selectedDate &&
                <div className="mt-2">
                    Kellaaegade nägemiseks vajuta päevale
                </div>
            }
            {selectedDate && (
                <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                    <h3 className="font-semibold">{format(selectedDate, "PPP", {locale: et})}</h3>
                    <p>{getAvailabilityForDate(selectedDate)}</p>
                </div>
            )}
        </div>
    );
};

export default AvailabilityCalendar;